import { useEffect, useRef, useState } from 'react';
import { useReducedMotion } from 'framer-motion';
import { Mic, Square } from 'lucide-react';
import { Reveal } from '@/components/Reveal';
import { HOME } from '@/constants/testIds';

const BAR_COUNT = 28;

const LABELS = {
    idle: 'Standby',
    live: 'Live microphone',
    simulated: 'Simulated input — mic unavailable',
};

export const VoiceTest = () => {
    const reduce = useReducedMotion();
    const [mode, setMode] = useState('idle');
    const [level, setLevel] = useState(0);
    const barRefs = useRef([]);
    const rafRef = useRef(0);
    const audioRef = useRef(null);

    const paint = (values) =>
        values.forEach((v, i) => {
            const bar = barRefs.current[i];
            if (bar) bar.style.transform = `scaleY(${Math.max(0.06, Math.min(1, v))})`;
        });

    const reset = () => {
        cancelAnimationFrame(rafRef.current);
        paint(Array.from({ length: BAR_COUNT }, () => 0.06));
        setLevel(0);
    };

    const stopEverything = () => {
        reset();
        if (audioRef.current) {
            const { ctx, stream } = audioRef.current;
            stream?.getTracks().forEach((t) => t.stop());
            ctx?.close?.();
            audioRef.current = null;
        }
        setMode('idle');
    };

    useEffect(
        () => () => {
            cancelAnimationFrame(rafRef.current);
            audioRef.current?.stream?.getTracks().forEach((t) => t.stop());
            audioRef.current?.ctx?.close?.();
        },
        [],
    );

    const start = async () => {
        reset();
        if (audioRef.current) {
            audioRef.current.stream?.getTracks().forEach((t) => t.stop());
            audioRef.current.ctx?.close?.();
            audioRef.current = null;
        }

        let analyser = null;
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const ctx = new (window.AudioContext || window.webkitAudioContext)();
            const source = ctx.createMediaStreamSource(stream);
            analyser = ctx.createAnalyser();
            analyser.fftSize = 64;
            source.connect(analyser);
            audioRef.current = { ctx, stream };
            setMode('live');
        } catch {
            setMode('simulated');
        }

        const data = new Uint8Array(32);
        let t = 0;
        let frame = 0;

        const tick = () => {
            frame += 1;
            const values = [];
            if (analyser) {
                analyser.getByteFrequencyData(data);
                for (let i = 0; i < BAR_COUNT; i++) {
                    values.push(data[Math.min(31, Math.floor((i * 32) / BAR_COUNT))] / 255);
                }
            } else {
                t += 0.045;
                for (let i = 0; i < BAR_COUNT; i++) {
                    values.push(
                        0.14 +
                            0.7 *
                                Math.abs(Math.sin(t * 1.6 + i * 0.55)) *
                                (0.55 + 0.45 * Math.sin(t * 0.7 + i * 0.31)),
                    );
                }
            }
            paint(values);
            if (frame % 12 === 0) {
                setLevel(Math.round((values.reduce((a, b) => a + b, 0) / BAR_COUNT) * 100));
            }
            rafRef.current = requestAnimationFrame(tick);
        };

        if (reduce) {
            if (!analyser) {
                paint(Array.from({ length: BAR_COUNT }, (_, i) => 0.3 + 0.3 * Math.abs(Math.sin(i * 0.7))));
            }
            return;
        }
        rafRef.current = requestAnimationFrame(tick);
    };

    return (
        <section id="voice-test" data-testid={HOME.voiceTest} className="relative py-24 md:py-32">
            <div className="mx-auto max-w-6xl px-6">
                <Reveal>
                    <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
                        <div>
                            <p className="font-mono text-xs uppercase tracking-[0.25em] text-aqua">Voice test</p>
                            <h2 className="mt-4 max-w-lg text-3xl font-light leading-tight tracking-tight sm:text-4xl lg:text-5xl">
                                See your signal before the session.
                            </h2>
                        </div>
                        <p className="max-w-sm text-sm leading-relaxed text-zinc-400">
                            A microphone-level display and voice-test mode, built into ENGINE. Here
                            is a working demo of the meter.
                        </p>
                    </div>
                </Reveal>

                <Reveal delay={0.1}>
                    <div className="relative mt-12 overflow-hidden rounded-3xl border border-white/10 bg-surface-2 p-6 md:p-10">
                        <div
                            aria-hidden="true"
                            className="pointer-events-none absolute -top-24 left-1/2 h-64 w-[640px] -translate-x-1/2 rounded-full opacity-15"
                            style={{ background: 'radial-gradient(closest-side, rgba(0,229,255,0.4), transparent)' }}
                        />

                        <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                            <span
                                data-testid={HOME.voiceTestStatusLabel}
                                className="inline-flex w-max items-center gap-2 rounded-full border border-white/10 bg-black/40 px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-300"
                            >
                                <span
                                    className={`h-1.5 w-1.5 rounded-full ${
                                        mode === 'live'
                                            ? 'bg-aqua'
                                            : mode === 'simulated'
                                              ? 'bg-amber-400'
                                              : 'bg-zinc-600'
                                    }`}
                                />
                                {LABELS[mode]}
                            </span>

                            {mode === 'idle' ? (
                                <button
                                    data-testid={HOME.voiceTestStart}
                                    onClick={start}
                                    className="inline-flex h-10 items-center gap-2 rounded-full bg-aqua px-5 text-sm font-medium text-ink transition-all duration-300 hover:shadow-glow hover:brightness-110"
                                >
                                    <Mic className="h-4 w-4" />
                                    Start voice test
                                </button>
                            ) : (
                                <button
                                    data-testid={HOME.voiceTestStop}
                                    onClick={stopEverything}
                                    className="inline-flex h-10 items-center gap-2 rounded-full border border-white/15 px-5 text-sm text-zinc-200 transition-colors duration-300 hover:border-white/40 hover:text-white"
                                >
                                    <Square className="h-3.5 w-3.5" />
                                    Stop
                                </button>
                            )}
                        </div>

                        <div
                            data-testid={HOME.voiceTestMeter}
                            className="relative mt-8 flex h-40 items-stretch gap-1 md:h-48 md:gap-1.5"
                        >
                            {Array.from({ length: BAR_COUNT }).map((_, i) => (
                                <span
                                    key={i}
                                    ref={(el) => (barRefs.current[i] = el)}
                                    className="flex-1 origin-bottom rounded-full bg-gradient-to-t from-aqua/80 to-aqua/25 transition-transform duration-75"
                                    style={{ transform: 'scaleY(0.06)' }}
                                />
                            ))}
                        </div>

                        <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                            <p className="text-xs text-zinc-500">
                                Runs entirely in your browser — nothing is recorded or uploaded.
                            </p>
                            <p
                                data-testid={HOME.voiceTestLevel}
                                className="font-mono text-xs tabular-nums text-zinc-400"
                            >
                                LEVEL {String(level).padStart(3, '0')}%
                            </p>
                        </div>
                    </div>
                </Reveal>
            </div>
        </section>
    );
};
